// import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import {
  useGetRepairPersonelAddress,
  useSetRepairersActivity,
  useSetRepairPersonelAddress,
  useUpdateRepairPersonelAddress,
} from 'app/core/api/company/api-company';
import { PageDetails } from 'app/core/api/company/api-company.types';

// import { CREATE_COMPANY_REQ_KEYS } from 'app/core/api/company/api-company.types';
import FetchifyAddressForm, {
  DetailedAddress,
} from 'app/shared/components/fetchify-address/FetchifyAddressForm';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import { Opt } from 'app/tools/general.types';
import {
  useEffectOnce,
  useObservableSend,
} from 'app/tools/hooks/misc-hooks';
import { useObservableEagerState } from 'observable-hooks';
import React, { useMemo, useState } from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { useHistory } from 'react-router-dom';
import { useMovePage } from '../create-business-profile/business-profile-utils';
import BottomPageAndSubmitActions from '../create-business-profile/input-pages/components/BottomActions';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

type FormValues = DetailedAddress;

type Props = {
  firstTime?: boolean;
  isIndividual?: boolean;
};
export default function CreatePersonalAddress({
  firstTime = false,
  isIndividual = false,
}: Props): JSX.Element {
  useSetPageTitle('Address');
  const history = useHistory();
  const { nextPage } = useMovePage();
  const { showSnack } = useShowInfo();

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Personal Address',
    };
    repairActivity(pageDetails).subscribe();
  });
  const createCoApi = useSetRepairPersonelAddress();
  const updateCoApi = useUpdateRepairPersonelAddress();
  const { resource: company, loading } =
    useGetRepairPersonelAddress();
  const defaultValues = loading ? undefined : company;

  const { error, sending } = firstTime ? createCoApi : updateCoApi;
  const { request$: createCo$ } = createCoApi;
  const { request$: updateCo$ } = updateCoApi;

  // Allows autosaving current entered values on prev page btn click
  /** setGetValues has to be in the form `() => func` otherwise the
   *  set state will call the function and evaluate it rather than
   *  saving the function entity (set state will always call a
   *  function if it is given as the parameter)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [getValues, setGetValues] = useState<
    () => Opt<Partial<FormValues>>
  >(() => undefined);

  // Trigger a submission programmatically (eg. submit btn outside
  // form component)
  const [sendCommand, command$] = useObservableSend<'submit'>();
  const editable$ = useMemo(
    () => new BehaviorSubject(firstTime),
    [firstTime]
  );
  const editable = useObservableEagerState(editable$);

  const onSubmit = (details: DetailedAddress) => {
    let request$: Observable<unknown>;
    const companyAddress = {
      ...details,
      // if country is missing because code is not found, backend
      // validation will suffice to let the user know
      country: details.country.code || '',
    };

    if (firstTime) {
      request$ = createCo$({
        ...companyAddress,
      });
    } else {
      request$ = updateCo$(companyAddress);
    }

    const successActionText = firstTime ? 'created' : 'updated';
    return request$.pipe(
      tap(() => {
        showSnack({
          message: `Company successfully ${successActionText}!`,
        });

        if (firstTime) nextPage(true);
        else history.goBack();
      })
    );
  };

  // Click cancel button -> get navigated back automatically
  // useEffectOnChanges(() => {
  //   if (!editable && !sending && !error) {
  //     history.goBack();
  //   }
  // }, [editable, error, history, sending]);

  return (
    <>
      <Body tiny>Asterisks (*) indicate required fields</Body>
      <ErrorText message={error} />
      <FetchifyAddressForm
        {...{
          defaultValues,
          error,
          onSubmit,
          command$,
          setGetValues,
          editable,
        }}
      />
      {firstTime ? (
        <BottomPageAndSubmitActions
          isSubmit
          onSubmit={() => sendCommand('submit')}
          isIndividual={isIndividual}
          {...{ sending }}
        />
      ) : (
        <BottomEditActions
          onSave={() => sendCommand('submit')}
          {...{ editable$, loading, sending }}
        />
      )}
    </>
  );
}
